import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { SessionProvider } from './contexts/SessionContext';
import './utils/refreshPermissions'; // Load permission refresh utilities
import './App.css';

// Lazy load components
const Login = lazy(() => import('./components/Auth/Login'));
const Register = lazy(() => import('./components/Auth/Register'));
const SecureRegister = lazy(() => import('./components/Auth/SecureRegister'));
const VerifyAccount = lazy(() => import('./components/Auth/VerifyAccount'));
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const Layout = lazy(() => import('./components/Layout/Layout'));
const DashboardContent = lazy(() => import('./components/Dashboard/DashboardContent'));
const Users = lazy(() => import('./components/Users/Users'));
const Tickets = lazy(() => import('./components/Tickets/Tickets'));
const TicketDetail = lazy(() => import('./components/Tickets/TicketDetail'));
const UnassignedTickets = lazy(() => import('./components/Tickets/UnassignedTickets'));
const ClosedTickets = lazy(() => import('./components/Tickets/ClosedTickets'));
const UserDetail = lazy(() => import('./components/Users/UserDetail'));
const MiscellaneousTickets = lazy(() => import('./components/Tickets/MiscellaneousTickets'));
const TicketListing = lazy(() => import('./components/Tickets/TicketListing'));
const Calendar = lazy(() => import('./components/Calendar/Calendar'));
const Tasks = lazy(() => import('./components/Tasks/Tasks'));
const Feedback = lazy(() => import('./components/Feedback/Feedback'));
const FeedbackCoverage = lazy(() => import('./components/Feedback/FeedbackCoverage'));
const FeedbackSummary = lazy(() => import('./components/Feedback/FeedbackSummary'));
const FeedbackReport = lazy(() => import('./components/Feedback/FeedbackReport'));
const SystemSettings = lazy(() => import('./components/SystemSettings/SystemSettings'));
const PublicForm = lazy(() => import('./components/PublicForm/PublicForm'));
const Clients = lazy(() => import('./components/Clients/Clients'));
const NewClientsPage = lazy(() => import('./components/Clients/NewClientsPage'));
const ClientDetailPage = lazy(() => import('./components/Clients/ClientDetailPage'));
const ClientBookingCalendar = lazy(() => import('./components/Calendar/ClientBookingCalendar'));
const Reports = lazy(() => import('./components/Reports/Reports'));
const ClientRequirements = lazy(() => import('./components/ClientRequirements/ClientRequirements'));
const AiDeskUpdates = lazy(() => import('./components/AiDeskUpdates/AiDeskUpdates'));

const LoadingScreen = () => <div className="loading-screen">Loading...</div>;

//test
function App() {
  return (
    <SessionProvider>
      <Router>
        <div className="App">
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/secure-register" element={<SecureRegister />} />
              <Route path="/verify-account" element={<VerifyAccount />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/support" element={<PublicForm />} />
              <Route path="/book/:token" element={<ClientBookingCalendar />} />

              {/* Protected Routes with Layout */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardContent />} />
                <Route path="users" element={<Users />} />

                <Route path="tickets" element={<Tickets />} />
                <Route path="tickets/:ticketId" element={<TicketDetail />} />
                <Route path="unassigned-tickets" element={<UnassignedTickets />} />
                <Route path="closed-tickets" element={<ClosedTickets />} />
                <Route path="users/:userId" element={<UserDetail />} />
                <Route path="ticket-listing" element={<TicketListing />} />
                <Route path="miscellaneous-tickets" element={<MiscellaneousTickets />} />
                <Route path="calendar" element={<Calendar />} />
                <Route path="tasks" element={<Tasks />} />
                <Route path="feedback" element={<Feedback />} />
                <Route path="feedback-coverage" element={<FeedbackCoverage />} />
                <Route path="feedback-summary" element={<FeedbackSummary />} />
                <Route path="feedback-report" element={<FeedbackReport />} />
                <Route path="system-settings" element={<SystemSettings />} />
                <Route path="clients" element={<Clients />} />
                <Route path="clients/:clientId" element={<ClientDetailPage />} />
                <Route path="new-clients" element={<NewClientsPage />} />
                <Route path="reports" element={<Reports />} />
                <Route path="client-requirements" element={<ClientRequirements />} />
                <Route path="ai-desk-updates" element={<AiDeskUpdates />} />
              </Route>
            </Routes>
          </Suspense>
        </div>
      </Router>
    </SessionProvider>
  );
}

export default App;
